"use client";

import React, { useState, useEffect } from 'react';
import { AlertCircle, X, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface BrowserExtensionWarningProps {
  className?: string;
}

export const BrowserExtensionWarning: React.FC<BrowserExtensionWarningProps> = ({ className }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check for common browser extension patterns
    const checkForExtensions = () => {
      const hasAdBlocker = 
        // Check for common ad blockers
        typeof window !== 'undefined' && (
          (window as any).adblock || 
          (window as any).adBlockDetected ||
          document.querySelector('#adblock-detected') ||
          // Check if requests are being blocked
          navigator.userAgent.includes('AdBlock') ||
          navigator.userAgent.includes('Adblock')
        );

      const hasPrivacyExtension = 
        // Check for privacy extensions that might block Firebase
        typeof window !== 'undefined' && (
          navigator.userAgent.includes('Privacy Badger') ||
          navigator.userAgent.includes('uBlock') ||
          navigator.userAgent.includes('Ghostery') ||
          navigator.userAgent.includes('Disconnect')
        );

      return hasAdBlocker || hasPrivacyExtension;
    };

    // Show warning if extensions are detected and not previously dismissed
    if (checkForExtensions() && !dismissed) {
      // Only show after a delay to avoid immediate popup
      const timer = setTimeout(() => {
        setShowWarning(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [dismissed]);

  const handleDismiss = () => {
    setShowWarning(false);
    setDismissed(true);
    // Store dismissal in localStorage
    localStorage.setItem('extension-warning-dismissed', 'true');
  };

  const handleWhitelist = () => {
    // Copy current domain to clipboard for easy whitelisting
    const domain = window.location.hostname;
    navigator.clipboard.writeText(domain);
    toast.success(`Domain ${domain} copied! Add it to your extension's whitelist.`);
  };

  // Check if previously dismissed
  useEffect(() => {
    const wasDismissed = localStorage.getItem('extension-warning-dismissed');
    if (wasDismissed) {
      setDismissed(true);
    }
  }, []);

  if (!showWarning || dismissed) return null;

  return (
    <div className={`fixed bottom-4 right-4 max-w-md z-50 animate-in slide-in-from-bottom-2 ${className}`}>
      <Alert className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950 shadow-lg">
        <Shield className="h-4 w-4 text-orange-600" />
        <AlertDescription className="text-sm">
          <div className="space-y-2">
            <p className="font-medium text-orange-800 dark:text-orange-200">
              Browser Extension Detected
            </p>
            <p className="text-orange-700 dark:text-orange-300">
              Some browser extensions (ad blockers, privacy tools) may block essential features like saving data, authentication, and real-time updates.
            </p>
            <div className="flex gap-2 pt-2">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleWhitelist}
                className="text-xs"
              >
                Copy Domain to Whitelist
              </Button>
              <Button 
                size="sm" 
                variant="ghost"
                onClick={handleDismiss}
                className="text-xs"
              >
                I'll disable it manually
              </Button>
            </div>
          </div>
        </AlertDescription>
        <Button
          variant="ghost"
          size="sm"
          className="absolute -top-2 -right-2 h-6 w-6 p-0"
          onClick={handleDismiss}
        >
          <X className="h-3 w-3" />
        </Button>
      </Alert>
    </div>
  );
};

export default BrowserExtensionWarning;
